import React from "react";
import AuthorizedPage from "../../models/base/AuthorizedPage";
import {Button, ButtonType, Icon, PageContainer, PageHeader, PageRow} from "@reapptor-apps/reapptor-react-components";
import CruiseOrder from "@/models/server/CruiseOrder";
import {ApiProvider, ReactUtility} from "@reapptor-apps/reapptor-react-common";
import CruiseSummary from "@/pages/Cruise/Contacts/CruiseSummary/CruiseSummary";
import CruiseController from "@/pages/CruiseController";
import Localizer from "@/localization/Localizer";

import styles from "./CruiseMyOrders.module.scss";

interface ICruiseMyOrdersProps {
}

interface ICruiseMyOrdersState {
    orders: CruiseOrder[];
    index: number;
    initialized: boolean;
}

export default class CruiseMyOrders extends AuthorizedPage<ICruiseMyOrdersProps, ICruiseMyOrdersState> {

    state: ICruiseMyOrdersState = {
        orders: [],
        index: -1,
        initialized: false
    };
    
    private readonly _hasReturnUrl: boolean = CruiseController.hasReturnUrl();
    
    private async prevAsync(): Promise<void> {
        await this.setState({ index: this.state.index - 1});
    }
    
    private async nextAsync(): Promise<void> {
        await this.setState({ index: this.state.index + 1});
    }
    
    private get orders(): CruiseOrder[] {
        return this.state.orders;
    }
    
    private get current(): CruiseOrder | null {
        return (this.state.index != -1) ? this.orders[this.state.index] : null;
    }
    
    private get contactPhone(): string {
        return this?.current?.country?.contactPhone ?? this.getContext().settings.contactPhone ?? "";
    }
    
    private get contactEmail(): string {
        return this?.current?.country?.contactEmail ?? "";
    }
    
    private get departBy(): string {
        return this?.current?.departBy?.toDateString() ?? "";
    }
    
    private get visitPoint(): string {
        return this?.current?.visitPoint ?? "";
    }
    
    private get arrivalAt(): string {
        return this?.current?.arriveAt?.toDateString() ?? ""
    }
    
    private get prev(): CruiseOrder | null {
        return ((this.state.index != -1) && (this.state.index > 0)) ? this.orders[this.state.index - 1] : null;
    }
    
    private get next(): CruiseOrder | null {
        return ((this.state.index != -1) && (this.state.index < this.orders.length - 1)) ? this.orders[this.state.index + 1] : null;
    }
    
    private get hasReturnUrl(): boolean {
        return this._hasReturnUrl;
    }

    public get hasTopNav(): boolean {
        return !this.hasReturnUrl;
    }

    public getTitle(): string {
        return Localizer.topNavMyOrders;
    }
    
    public async initializeAsync(): Promise<void> {
        await super.initializeAsync();
        
        const orders: CruiseOrder[] = await ApiProvider.postAsync("api/cruise/listMyOrders");
        
        const index: number = (orders.length > 0) ? 0 : -1;
        
        await this.setState({ orders, index, initialized: true });
    }

    public render(): React.ReactNode {
        const hasReturnUrlStyle = this.hasReturnUrl && styles.hasReturnUrl;
        
        return (
            <PageContainer className={this.css(styles.cruiseMyOrders, this.mobile && styles.mobile)}>

                <PageHeader title={this.getTitle()} className={this.css(hasReturnUrlStyle)}>

                    {
                        (this.hasReturnUrl) &&
                        (
                            <div className={styles.close}>
                                <Icon name={"fal fa-times-circle"} onClick={() => CruiseController.closeAsync()} />
                            </div>
                        )
                    }
                    
                </PageHeader>

                <PageRow>
                    <div className="col">

                        {
                            (this.state.initialized) &&
                            (
                                <React.Fragment>

                                    { (this.orders.length == 0) && <span className={styles.noOrders}>{Localizer.cruiseMyOrdersPageNoOrders}</span> }

                                    {
                                        (this.current) &&
                                        (
                                            <div className={styles.current}>
                                                
                                                <div className={styles.toolbar}>

                                                    <Button icon={{name: "arrow-left"}}
                                                            title={Localizer.cruiseMyOrdersPagePrevious}
                                                            type={ButtonType.Primary}
                                                            disabled={this.prev == null}
                                                            onClick={() => this.prevAsync()}
                                                    />
    
                                                    <span className={styles.label}>
                                                        {this.current.date.format("D")}
                                                    </span>
    
                                                    <Button icon={{name: "arrow-right"}}
                                                            title={Localizer.cruiseMyOrdersPageNext}
                                                            type={ButtonType.Primary}
                                                            disabled={this.next == null}
                                                            onClick={() => this.nextAsync()}
                                                    />

                                                </div>
                                                
                                                <div className={styles.status}>
                                                    {
                                                        (this.current.country) &&
                                                        (
                                                            <p>
                                                                <span>{Localizer.cruiseMyOrdersPageCountry}</span>
                                                                {"{0} ({1})".format(this.current.country.nativeName, this.current.country.name)}
                                                            </p>
                                                        )
                                                    }
                                                    <p>
                                                        <span>{Localizer.cruiseMyOrdersPageOrderedAt}</span>
                                                        {this.current.createdAt.format("D")}
                                                    </p>
                                                    <p>
                                                        <span>{Localizer.cruiseMyOrdersPageStatus}</span>
                                                        {ReactUtility.toTags("<b>{0:CruiseOrderState}</b>".format(this.current.state))}
                                                    </p>
                                                    <p>
                                                        <span>{Localizer.cruiseMyOrdersPageCaptain}</span>
                                                        {"{0}".format(this.current.captain ?? Localizer.cruiseMyOrdersPageNoAssigned)}
                                                    </p>
                                                    <p>
                                                        <span>{Localizer.cruiseMyOrdersPageCall}</span>
                                                        <a href={`tel:${this.contactPhone}`}>{this.contactPhone}</a>
                                                    </p>
                                                    <p>
                                                        <span>{Localizer.cruiseMyOrdersPageEmail}</span>
                                                        <a href={`mailto:${this.contactEmail}`}>{this.contactEmail}</a>
                                                    </p>
                                                    <p>
                                                        <span>{Localizer.cruiseOrdersDepartBy}</span>
                                                        {this.departBy}
                                                    </p>
                                                    <p>
                                                        <span>{Localizer.cruiseOrdersVisitPoint}</span>
                                                        {this.visitPoint}
                                                    </p>
                                                    <p>
                                                        <span>{Localizer.cruiseOrdersArrivalAt}</span>
                                                        {this.arrivalAt}
                                                    </p>
                                                </div>

                                                <div className={styles.wave} />

                                                <div>
                                                    
                                                    <CruiseSummary order={this.current} />
                                                    
                                                </div>
                                                
                                            </div>
                                        )
                                    }
                                    
                                </React.Fragment>
                            )
                        }

                    </div>
                </PageRow>

            </PageContainer>
        );
    }
}